import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { BotMessageSquare, DatabaseZap, RefreshCcwDot, SearchCode } from 'lucide-react';
import { motion } from 'motion/react';
interface TimelineEvent {
  phase: string;
  title: string;
  description: React.ReactNode;
  icon: React.ReactNode;
  timeline: string;
}

const timelineEvents: TimelineEvent[] = [
  {
    phase: 'Phase 1',
    title: 'Core Engine',
    description: (
      <div className="space-y-2 text-base">
        <ul className="list-disc pl-5 text-lg text-gray-300">
          <li>MVP with foundational rug check capabilities.</li>
          <li>Build rugger-info databases for future model training.</li>
          <li>Host enhanced tools through partnerships.</li>
          <li>Introduce premium services to foster token utility.</li>
        </ul>
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem className="border-none w-full" value="item-1">
            <AccordionTrigger className="py-2 cursor-pointer text-purple-500 hover:text-purple-700 text-lg">
              Enhanced Tools
            </AccordionTrigger>
            <AccordionContent>
              <ul className="list-disc ml-3 pl-5 text-base text-gray-300">
                <li>Inspect code to determine if "gud tek"</li>
                <li>X/TG/Discord sentiment analysis</li>
                <li>Advanced wallet bundling and sniper info</li>
                <li>Whale wallet tracking</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    ),
    icon: <SearchCode />,
    timeline: 'Q1 2025',
  },
  {
    phase: 'Phase 2',
    title: 'Multi-Agent System',
    description: (
      <div className="space-y-2 text-base">
        <ul className="list-disc pl-5 text-lg text-gray-300">
          <li>Deploy @Antirug_ai on X, Telegram, and Discord with Phase 1 tools.</li>
          <li>Launch SWARM of agents for niche tasks using Phase 1 data.</li>
        </ul>
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem className="border-none w-full" value="item-1">
            <AccordionTrigger className="py-2 cursor-pointer text-purple-500 hover:text-purple-700 text-lg">
              Agent Specialties
            </AccordionTrigger>
            <AccordionContent>
              <ul className="list-disc ml-3 pl-5 text-base text-gray-300">
                <li>Early Alpha Calls</li>
                <li>Copy Trading</li>
                <li>Yield Farming</li>
                <li>Crypto News Analysis</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    ),
    icon: <DatabaseZap />,
    timeline: 'Q1 2025',
  },
  {
    phase: 'Phase 3',
    title: 'Customizable Infrastructure',
    description: (
      <div className="space-y-2 text-base">
        <ul className="list-disc pl-5 text-lg text-gray-300">
          <li>"Build Your Own" AI agents with tailored tools with no code.</li>
          <li>Deploy your agents to X, Telegram, Discord, Whatsapp, and more.</li>
          <li>Launch a premium tier system to enhance token circulation.</li>
        </ul>
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem className="border-none w-full" value="item-1">
            <AccordionTrigger className="py-2 cursor-pointer text-purple-500 hover:text-purple-700 text-lg">
              Example Agent
            </AccordionTrigger>
            <AccordionContent>
              <ul className="list-disc ml-3 pl-5 text-base text-gray-300">
                <li>
                  My agent uses <strong>alpha calls</strong> and <strong>whale tracking</strong>{' '}
                  tools, so that I can make more informed trading decisions.
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    ),
    icon: <BotMessageSquare />,
    timeline: 'Q2 2025',
  },
  {
    phase: 'Phase 4',
    title: 'Continuous Improvement',
    description: (
      <div className="space-y-2 text-base">
        <ul className="list-disc pl-5 text-lg text-gray-300">
          <li>Expand across chains: BASE, ETH, MATIC, BNB, etc.</li>
          <li>Iterate on Phases 2 & 3 for continuous improvement</li>
        </ul>
      </div>
    ),
    icon: <RefreshCcwDot />,
    timeline: 'Q3 2025',
  },
];

const MotionCard = motion(Card);

const RoadMapMobileCard = ({ item, index }: { item: TimelineEvent; index: number }) => {
  return (
    <div className="relative mt-auto h-fit box-border">
      <motion.div
        initial={{
          scale: 0,
          translateY: '-50%',
        }}
        animate={{
          scale: 1,
          translateY: '-50%',
        }}
        className="absolute top-1/2 z-[9] size-12 bg-blue-900 border-2 border-purple-400 rounded-full text-purple-400 flex items-center justify-center"
      >
        {item.icon}
      </motion.div>
      <MotionCard
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
          transition: {
            delay: index * 0.025,
          },
        }}
        key={item.title}
        className={cn('bg-transparent border-[rgb(34,50,83)] flex-row ml-10')}
      >
        <div className="flex flex-col">
          <CardHeader className="pb-2 flex flex-col">
            <div className="flex items-center">
              <p className="bg-purple-700 text-white rounded-full w-fit px-3 py-1 text-sm">
                {item.phase}
              </p>
              <p className="text-sm text-gray-400 ml-2">{item.timeline}</p>
            </div>
            <CardTitle className="text-xl font-semibold text-purple-400 flex flex-row gap-3 items-center justify-start">
              {item.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-gray-200 w-full">{item.description}</CardContent>
        </div>
      </MotionCard>
    </div>
  );
};

const RoadMapCard = ({
  item,
  index,
  className,
}: {
  item: TimelineEvent;
  index: number;
  className?: string;
}) => {
  return (
    <div className={cn('relative h-fit box-border', index % 2 === 0 ? 'pr-4' : 'pl-4')}>
      <MotionCard
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 1,
          transition: {
            delay: index * 0.025,
            velocity: 1,
          },
        }}
        key={item.title}
        className={cn(
          'w-full bg-transparent border-[rgb(34,50,83)] hover:border-purple-400 transition-all ml-0 cursor-pointer',
          index % 2 === 0 ? 'hover:-translate-x-2' : 'hover:translate-x-2',
          className,
        )}
      >
        <CardHeader className="pb-2 flex flex-col">
          <p className="bg-purple-700 text-white rounded-full w-fit px-3 py-1 text-sm">
            {item.phase}
          </p>
          <CardTitle className="text-xl font-semibold text-purple-400 flex flex-row gap-3 items-center justify-start">
            {item.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-gray-200">{item.description}</CardContent>
      </MotionCard>
    </div>
  );
};

export const Timeline = () => {
  return (
    <>
      <div className="relative block md:hidden pl-2 pr-4">
        <div className="absolute left-[22px] flex flex-col items-center h-full">
          <motion.div
            initial={{
              scale: 0,
            }}
            animate={{
              scale: 1,
            }}
            className="size-5 bg-blue-900 border-[3px] border-purple-400 rounded-full"
          ></motion.div>

          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            className="w-0.5 bg-purple-400 flex-1"
          ></motion.div>

          <motion.div
            initial={{
              scale: 0,
            }}
            animate={{
              scale: 1,
            }}
            className="size-5 bg-blue-900 border-[3px] border-purple-400 rounded-full"
          ></motion.div>
        </div>

        <div className="space-y-16 py-16 mb-8">
          {timelineEvents.map((item, index) => (
            <RoadMapMobileCard key={index} index={index} item={item} />
          ))}
        </div>
      </div>
      <div className="relative hidden md:flex justify-center w-full">
        <div className="flex-1 flex flex-col items-start my-12">
          {timelineEvents.map((item, index) => (
            <div
              key={index}
              style={{
                marginTop: index === 0 ? undefined : '-50px',
              }}
              className={cn('relative w-1/2', index % 2 === 0 ? '' : 'ml-auto')}
            >
              <motion.div
                initial={{
                  scale: 0,
                  translateY: '-50%',
                  translateX: index % 2 === 0 ? '50%' : '-50%',
                }}
                animate={{
                  scale: 1,
                  translateY: '-50%',
                  translateX: index % 2 === 0 ? '50%' : '-50%',
                }}
                className={cn(
                  'peer absolute size-12 bg-blue-900 border-2 border-purple-400 rounded-full text-purple-400 flex items-center justify-center z-10 top-1/2',
                  index % 2 === 0 ? 'right-0' : 'left-0',
                )}
              >
                {item.icon}
              </motion.div>
              <RoadMapCard item={item} index={index} />
            </div>
          ))}
        </div>

        <div className="absolute left-1/2 -translate-x-1/2 h-full">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{
              opacity: 1,
            }}
            className="absolute h-full w-0.5 bg-purple-400"
          />
          <motion.div
            initial={{ scale: 0 }}
            animate={{
              scale: 1,
            }}
            className="absolute -left-3.5 size-7 top-0 bg-blue-900 border-[3px] border-purple-400 rounded-full"
          />
          <motion.div
            initial={{ scale: 0 }}
            animate={{
              scale: 1,
            }}
            className="absolute -left-3.5 size-7 bottom-0 bg-blue-900 border-[3px] border-purple-400 rounded-full"
          />
        </div>
      </div>
    </>
  );
};
